import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { useCart } from "../context/CartContext";
import Stars from "../components/Stars";
import { ChevronRight, ShoppingCart, Truck, ShieldCheck } from "lucide-react";

const EmergencyToolDetail = () => {
  const { id } = useParams();
  const [tool, setTool] = useState(null);
  const [related, setRelated] = useState([]);
  const [activeImg, setActiveImg] = useState(0);
  const [qty, setQty] = useState(1);
  const { addItem } = useCart();

  useEffect(() => {
    api.get(`/emergency-tools/${id}`).then((r) => { setTool(r.data); setActiveImg(0); });
    api.get(`/emergency-tools/${id}/related`).then((r) => setRelated(r.data)).catch(() => setRelated([]));
  }, [id]);

  if (!tool) return <div className="vd-container py-20"><p className="vd-muted">Loading...</p></div>;

  const images = tool.gallery && tool.gallery.length ? tool.gallery : [tool.image];

  return (
    <div className="vd-page" data-testid="page-tool-detail">
      <div className="vd-container py-4 flex items-center gap-2 text-sm">
        <Link to="/" className="vd-muted hover:text-[var(--primary-red)]">Home</Link>
        <ChevronRight size={14} color="var(--text-muted)" />
        <Link to="/emergency-tools" className="vd-muted hover:text-[var(--primary-red)]">Emergency Tools</Link>
        <ChevronRight size={14} color="var(--text-muted)" />
        <span className="vd-heading font-semibold">{tool.name}</span>
      </div>

      <section className="vd-container py-6 grid grid-cols-1 lg:grid-cols-2 gap-10">
        <div>
          <div className="vd-card overflow-hidden aspect-square flex items-center justify-center" style={{ background: "var(--bg-secondary)" }}>
            <img src={images[activeImg]} alt={tool.name} className="w-full h-full object-contain p-6" data-testid="tool-main-image" />
          </div>
          <div className="flex gap-3 mt-3">
            {images.map((src, i) => (
              <button
                key={i}
                onClick={() => setActiveImg(i)}
                className="rounded-md overflow-hidden"
                style={{ border: activeImg === i ? "2px solid var(--primary-red)" : "1px solid var(--border-color)", width: 80, height: 80, background: "var(--bg-secondary)" }}
                data-testid={`tool-thumb-${i}`}
              >
                <img src={src} alt="" className="w-full h-full object-contain p-1" />
              </button>
            ))}
          </div>
        </div>

        <div>
          <p className="vd-muted uppercase tracking-widest text-xs mb-2">{tool.brand}</p>
          <h1 className="mb-3" style={{ fontSize: 36 }}>{tool.name}</h1>
          {tool.spec_subtitle && <p className="vd-body text-lg mb-4">{tool.spec_subtitle}</p>}
          <div className="flex items-center gap-3 mb-5">
            <Stars value={tool.rating} size={18} />
            <span className="vd-muted text-sm">{tool.rating.toFixed(1)} / 5</span>
          </div>
          <p className="font-extrabold mb-6" style={{ color: "var(--primary-red)", fontSize: 36 }} data-testid="tool-price">£{tool.price.toFixed(2)}</p>

          <p className="vd-body mb-6">{tool.description}</p>

          <div className="flex items-center gap-3 mb-6">
            <label className="vd-muted text-sm">Quantity</label>
            <div className="inline-flex items-center border rounded-md" style={{ borderColor: "var(--border-color)" }}>
              <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="px-3 py-2 vd-heading">−</button>
              <span className="px-4 vd-heading font-semibold">{qty}</span>
              <button onClick={() => setQty((q) => q + 1)} className="px-3 py-2 vd-heading">+</button>
            </div>
          </div>

          <div className="flex flex-wrap gap-3 mb-8">
            <button
              onClick={() => addItem({ tyre_id: `tool-${tool.id}`, name: tool.name, price: tool.price, image: tool.image }, qty)}
              className="vd-btn-primary"
              data-testid="tool-add-to-basket"
            ><ShoppingCart size={18} /> Add to Basket</button>
            <Link to="/emergency-tools" className="vd-btn-secondary" data-testid="tool-back-to-list">Back to Tools</Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <div className="flex items-center gap-2 vd-body"><Truck size={18} color="var(--primary-red)" /> Free UK Delivery over £50</div>
            <div className="flex items-center gap-2 vd-body"><ShieldCheck size={18} color="var(--primary-red)" /> 12-Month Warranty</div>
          </div>
        </div>
      </section>

      {related.length > 0 && (
        <section className="vd-container pb-16">
          <h3 className="mb-6">Related Products</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {related.map((t) => (
              <article key={t.id} className="vd-card overflow-hidden flex flex-col">
                <Link to={`/emergency-tools/${t.id}`} className="block">
                  <div className="aspect-[4/3] overflow-hidden" style={{ background: "var(--bg-secondary)" }}>
                    <img src={t.image} alt={t.name} className="w-full h-full object-cover" />
                  </div>
                </Link>
                <div className="p-5 flex flex-col flex-1">
                  <span className="vd-muted text-xs uppercase tracking-wider">{t.brand}</span>
                  <Link to={`/emergency-tools/${t.id}`} className="vd-heading font-semibold mt-1">{t.name}</Link>
                  {t.spec_subtitle && <p className="vd-muted text-sm mt-1">{t.spec_subtitle}</p>}
                  <p className="font-extrabold mt-3" style={{ color: "var(--primary-red)", fontSize: 20 }}>£{t.price.toFixed(2)}</p>
                </div>
              </article>
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

export default EmergencyToolDetail;
